<template>
  <Slideover :visible="visible">
    <header class="px-2 py-3 sm:px-6 bg-green-600 text-white">
      <div class="flex items-start justify-between space-x-3">
        <h2 class="text-lg leading-7 font-medium">
          {{ title }}: {{ this.itemToBeRevised.SKU }}
        </h2>
        <div class="h-7 flex items-center">
          <button
            type="button"
            aria-label="Close panel"
            @click="$emit('toggle-slideover')"
            class="text-green-300 hover:text-white transition ease-in-out duration-150"
          >
            <icon set="solid" name="x" class="flex-shrink-0 m-1 h-5 w-5" />
          </button>
        </div>
      </div>
    </header>

    <!-- Main -->
    <div class="flex flex-col py-3 px-2 mx-4">
      <div class="mb-3">
        <label
          :for="`sku_${_uid}`"
          class="block text-sm font-medium leading-5 text-gray-700"
        >
          SKU:
        </label>
        <div class="mt-1 relative rounded-md shadow-sm">
          <input
            v-model="sku"
            type="text"
            :id="`sku_${_uid}`"
            :list="`skus_${_uid}`"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:cursor-not-allowed disabled:opacity-50"
            :disabled="loadingSkus"
            @change="skuChanged"
          />
          <datalist :id="`skus_${_uid}`">
            <option v-for="sku in filteredSkus" :key="sku">{{ sku }}</option>
          </datalist>
          <div
            v-if="loadingSkus"
            class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
          >
            <LoadingSpinner class="text-gray-500" />
          </div>
        </div>
      </div>

      <div class="grid grid-cols-1 gap-y-2 gap-x-4 sm:grid-cols-6">
        <!-- QOH -->
        <div class="sm:col-span-3">
          <label
            :for="`qoh_${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            QOH:
          </label>
          <div class="mt-1 relative rounded-md shadow-sm">
            <select
              :id="`qoh_${_uid}`"
              v-model="facility_id"
              @change="validateForRestrictions"
              :disabled="loadingRestrictions"
              class="mt-1 form-select block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5"
            >
              <option v-for="facility in this.facilitiesWithQoh" :key="facility.id" :value="facility.id" :disabled="facility.qoh === 0">
                {{ facility.name }} : {{ facility.qoh }}
              </option>
            </select>
            <div
              v-if="loadingSkus"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <LoadingSpinner class="text-gray-500" />
            </div>
            <div
              v-if="loadingRestrictions"
              class="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none"
            >
              <LoadingSpinner class="text-gray-500" />
            </div>
          </div>
        </div>
        <div class="sm:col-span-3">
          <label
            :for="`price_per_unit_${_uid}`"
            class="block text-sm font-medium leading-5 text-gray-700"
          >
            Price per Unit:
          </label>
          <input
            :value="formattedPricePerUnit"
            @change="pricePerUnit = $event.target.value"
            disabled
            type="number"
            step="0.01"
            :id="`price_per_unit_${_uid}`"
            class="mt-1 form-input block w-full py-2 px-3 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:shadow-outline-blue focus:border-blue-300 transition duration-150 ease-in-out sm:text-sm sm:leading-5 disabled:cursor-not-allowed disabled:opacity-50"
          />
        </div>
      </div>
    </div>

    <HttpError :error="error" />
    <FulfillmentError :in-fulfillment="inFulfillment" action="Revise" />
    <!-- Footer -->
    <div class="flex justify-end px-4 py-4 space-x-4">
      <span class="inline-flex rounded-md shadow-sm">
        <button
          type="button"
          @click="$emit('toggle-slideover')"
          class="py-2 px-4 border border-gray-300 rounded-md text-sm leading-5 font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue active:bg-gray-50 active:text-gray-800 transition duration-150 ease-in-out"
        >
          Cancel
        </button>
      </span>
      <span class="inline-flex rounded-md shadow-sm">
        <Button
          @click-handler="process"
          :disabled="!canProcess || processing || !validated"
          class="flex items-center space-x-2"
        >
          <span>Revise Item</span>
          <LoadingSpinner v-if="processing" class="text-white" />
        </Button>
      </span>
    </div>
  </Slideover>
</template>
<script>
import { ApiFactory } from "@/api/ApiFactory.js";
import Button from "@/components/elements/Button.vue";
import Slideover from "@/components/layout/Slideover.vue";
import FulfillmentError from "@/components/elements/FulfillmentError";

const Order = ApiFactory.get("orders");
const Inventory = ApiFactory.get("inventories");
const Utilities = ApiFactory.get("utilities");

export default {
  name: "ReviseItem",
  components: {
    FulfillmentError,
    Button,
    Slideover,
  },
  props: {
    title: {
      type: String,
      default: "Title",
    },
    visible: {
      type: Boolean,
      default: false,
    },
    itemToBeRevised: {
      type: Object,
      required: true,
    },
    shipState: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      sku: this.itemToBeRevised.SKU,
      pricePerUnit: this.itemToBeRevised.PricePerUnit,
      skus: [],
      facilitiesWithQoh: [{ id: 0, name: "Please Select Valid SKU", qoh: 0}],
      skuqoh: null,
      error: null,
      processing: false,
      loadingSkus: false,
      loadingRestrictions: false,
      validating: false,
      validated: false,
      facility_id: null,
    };
  },
  computed: {
    order() {
      return this.$store.getters["orders/get"]("order");
    },
    orderStatus() {
      return this.order.status.Status;
    },
    inFulfillment() {
      return this.orderStatus === "In Fulfillment";
    },
    formattedPricePerUnit() {
      return Number(this.pricePerUnit).toFixed(2);
    },
    skuList() {
      return this.skus.map((entry) => entry.LocalSKU);
    },
    filteredSkus() {
      return this.skuList
        .filter((inventorySku) =>
          inventorySku.toLowerCase().includes(String(this.sku).toLowerCase())
        )
        .slice(0, 20);
    },
    canProcess() {
      if (this.inFulfillment) {
        return false;
      }

      if (this.sku === this.itemToBeRevised.SKU) {
        return false;
      }

      if (!this.sku || this.itemToBeRevised.adjCount > 0) {
        return false;
      }

      let element = this.skus.find((entry) => entry.LocalSKU === this.sku);
      if (
        element === undefined || !element
      ) {
        return false;
      }

      const selectedFacility = this.facilitiesWithQoh.find(facility => facility.id === this.facility_id);
      if (selectedFacility === undefined || !selectedFacility || Number(selectedFacility.qoh) <= 0) {
        return false;
      }

      return true;
    },
  },
  watch: {
    itemToBeRevised(value) {
      this.sku = value.SKU;
      this.pricePerUnit = value.PricePerUnit;
      this.error = null;
    },
    skuqoh: {
      immediate: true,
      handler(newSkuqoh) {
        if (!newSkuqoh || newSkuqoh.length === 0 || !newSkuqoh[0] || newSkuqoh[0] === undefined) {
          this.facilitiesWithQoh = [{ id: 0, name: "Please Select Valid SKU", qoh: 0}];
        } else {
          const _skuqoh = newSkuqoh[0];
          this.facilitiesWithQoh = [
            { id: 1, name: "Fort Mill, SC", qoh: parseInt(_skuqoh.FC1TrueQOH,0) },
            { id: 3, name: "Dundee, OR", qoh: parseInt(_skuqoh.FC3TrueQOH,0) },
          ];
        }
      },
    },
  },
  created() {
    this.loadInventorySkus();
  },
  methods: {
    async loadInventorySkus() {
      this.loadingSkus = true;
      Inventory.skus()
        .then((response) => (this.skus = response.data.body))
        .finally(() => (this.loadingSkus = false));
    },
    async validateForRestrictions() {
      try {
        if (this.facility_id == null) {
          return;
        }
        this.loadingRestrictions = true;
        this.validated = false;
        this.error = null;

        const response = await Utilities.validateSkuStateRestrictionByFacility({ skus: [this.sku], facility_id: this.facility_id });

        const res = response.data.body;
        const facility = res[this.sku].find(
            facilityRestrictions => facilityRestrictions.facility_id === this.facility_id
        );
        if (facility === undefined || facility.state_restrictions.includes(this.shipState)) {
          this.validated = false;
          const err = new Error("Failed validation");
          err.response = {
            data: {
              message: `${this.sku} is restricted in ${this.shipState}`,
            },
          };
          throw err;
        }

        this.validated = true;
      } catch (error) {
        this.error = error;
      } finally {
        this.loadingRestrictions = false;
      }
    },
    async skuChanged() {
      this.loadingSkus = true;
      this.error = null;
      try {
        const qohResponse = await Inventory.qohByFacility(this.sku);
        this.$set(this, 'skuqoh', qohResponse.data.body);

        await this.validateForRestrictions();

      } catch (error) {
        this.error = error;
      } finally {
        this.loadingSkus = false;
      }
    },
    process() {
      this.processing = true;

      Order.reviseItem(
        this.itemToBeRevised.order.SourceOrderNumber,
        this.itemToBeRevised.ItemNumber,
        {
          originalSku: this.itemToBeRevised.SKU,
          sku: this.sku,
          pricePerUnit: this.pricePerUnit,
          facilityId: this.facility_id
        }
      )
        .then(this.handleSuccess)
        .catch((error) => (this.error = error))
        .finally(() => (this.processing = false));
    },
    handleSuccess() {
      this.$emit("toggle-slideover");
      this.$emit("refresh-order");
    },
  },
};
</script>
